import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { format, eachMonthOfInterval, endOfMonth } from "date-fns";
import { sv } from "date-fns/locale";
import { formatPrice } from "@/utils/formatPrice";
import { Crown } from "lucide-react";
import ExportDropdown from "./ExportDropdown";
import type { ReportDateRange } from "./ReportDateRangePicker";

interface MembershipWithPlan {
  id: string;
  user_id: string;
  status: string;
  created_at: string;
  current_period_start: string | null;
  current_period_end: string | null;
  plan_name: string | null;
  plan_price: number | null;
  plan_interval: string | null;
}

interface Props {
  range: ReportDateRange;
  memberships: MembershipWithPlan[];
  compareEnabled: boolean;
}

function monthlyPrice(price: number, interval: string | null): number {
  if (interval === "quarter") return Math.round(price / 3);
  if (interval === "year") return Math.round(price / 12);
  return price; // month or null
}

const STATUS_COLORS: Record<string, string> = {
  active: "default",
  cancelled: "destructive",
  expired: "secondary",
};

// STATUS_LABELS moved into the component to use t()

export default function MembershipTab({ range, memberships, compareEnabled: _compareEnabled }: Props) {
  const { t } = useTranslation("admin");

  const STATUS_LABELS: Record<string, string> = {
    active: t("reports.membership.statusActive"),
    cancelled: t("reports.membership.statusCancelled"),
    expired: t("reports.membership.statusExpired"),
  };

  // KPI computations
  const active = useMemo(
    () => memberships.filter((m) => m.status === "active"),
    [memberships],
  );

  const mrr = useMemo(
    () =>
      active.reduce(
        (sum, m) => sum + monthlyPrice(m.plan_price ?? 0, m.plan_interval),
        0,
      ),
    [active],
  );

  const newThisPeriod = useMemo(
    () =>
      memberships.filter((m) => {
        const created = new Date(m.created_at);
        return created >= range.from && created <= range.to;
      }),
    [memberships, range],
  );

  const cancelledThisPeriod = useMemo(
    () =>
      memberships.filter((m) => {
        if (m.status !== "cancelled") return false;
        if (!m.current_period_end) return false;
        const end = new Date(m.current_period_end);
        return end >= range.from && end <= range.to;
      }),
    [memberships, range],
  );

  // MRR trend data
  const mrrTrend = useMemo(() => {
    const months = eachMonthOfInterval({ start: range.from, end: range.to });
    return months.map((monthStart) => {
      const monthEnd = endOfMonth(monthStart);
      const activeInMonth = memberships.filter((m) => {
        const created = new Date(m.created_at);
        if (created > monthEnd) return false;
        if (m.current_period_end) {
          const end = new Date(m.current_period_end);
          if (end < monthStart) return false;
        }
        if (m.status === "cancelled" && m.current_period_end) {
          const end = new Date(m.current_period_end);
          if (end < monthStart) return false;
        }
        return m.status === "active" || (m.status === "cancelled" && m.current_period_end && new Date(m.current_period_end) > monthStart);
      });
      const monthMrr = activeInMonth.reduce(
        (sum, m) => sum + monthlyPrice(m.plan_price ?? 0, m.plan_interval),
        0,
      );
      return {
        month: format(monthStart, "MMM yy", { locale: sv }),
        MRR: monthMrr,
      };
    });
  }, [memberships, range]);

  // Per plan breakdown
  const planBreakdown = useMemo(() => {
    const groups: Record<string, { count: number; mrr: number }> = {};
    for (const m of active) {
      const plan = m.plan_name || t("reports.membership.unknownPlan");
      if (!groups[plan]) groups[plan] = { count: 0, mrr: 0 };
      groups[plan].count++;
      groups[plan].mrr += monthlyPrice(m.plan_price ?? 0, m.plan_interval);
    }
    const totalMrr = Object.values(groups).reduce((s, g) => s + g.mrr, 0);
    return Object.entries(groups)
      .map(([plan, data]) => ({
        plan,
        count: data.count,
        mrr: data.mrr,
        share: totalMrr > 0 ? Math.round((data.mrr / totalMrr) * 100) : 0,
      }))
      .sort((a, b) => b.mrr - a.mrr);
  }, [active]);

  // Status breakdown
  const statusBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const m of memberships) {
      const s = m.status || "unknown";
      counts[s] = (counts[s] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([status, count]) => ({
        status,
        label: STATUS_LABELS[status] || status,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [memberships]);

  const statusChartData = useMemo(
    () => statusBreakdown.map((s) => ({ name: s.label, [t("reports.membership.count")]: s.count })),
    [statusBreakdown],
  );

  // Export
  const exportData = () =>
    planBreakdown.map((r) => ({
      [t("reports.membership.plan")]: r.plan,
      [t("reports.membership.activePlural")]: r.count,
      "MRR": r.mrr,
      [t("reports.membership.share")]: r.share,
    }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{t("reports.membership.title")}</span>
        </div>
        <ExportDropdown getData={exportData} filePrefix="medlemskap" />
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t("reports.membership.activeMemberships")}</p>
            <p className="text-2xl font-bold">{active.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">MRR</p>
            <p className="text-2xl font-bold">{formatPrice(mrr)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t("reports.membership.newThisPeriod")}</p>
            <p className="text-2xl font-bold">{newThisPeriod.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{t("reports.membership.cancelled")}</p>
            <p className="text-2xl font-bold">{cancelledThisPeriod.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* MRR trend */}
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm">{t("reports.membership.mrrTrend")}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={mrrTrend}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}`} />
              <Tooltip formatter={(v: number) => formatPrice(v)} />
              <Line
                type="monotone"
                dataKey="MRR"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Per plan breakdown */}
      {planBreakdown.length > 0 && (
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm">{t("reports.membership.perPlan")}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("reports.membership.plan")}</TableHead>
                  <TableHead className="text-right">{t("reports.membership.activePlural")}</TableHead>
                  <TableHead className="text-right">MRR</TableHead>
                  <TableHead className="text-right">{t("reports.membership.share")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {planBreakdown.map((r) => (
                  <TableRow key={r.plan}>
                    <TableCell className="font-medium">{r.plan}</TableCell>
                    <TableCell className="text-right">{r.count}</TableCell>
                    <TableCell className="text-right">{formatPrice(r.mrr)}</TableCell>
                    <TableCell className="text-right">{r.share}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Status breakdown */}
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm">{t("reports.membership.byStatus")}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="flex flex-col gap-4">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={statusChartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey={t("reports.membership.count")} fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2">
              {statusBreakdown.map((s) => (
                <Badge
                  key={s.status}
                  variant={(STATUS_COLORS[s.status] as "default" | "destructive" | "secondary") || "outline"}
                >
                  {s.label}: {s.count}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
